import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Check, Coins, Send } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useLang } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WALLET = 'TDPGY395wwWGxjBAj9nciHQ22tVmcTF1tg';

export default function CryptoPayment({ lookupKey, amount }) {
  const { t, lang } = useLang();
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', contact: '' });
  const [sending, setSending] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(WALLET);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard unavailable */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/crypto/notify`, { ...form, product: lookupKey, language: lang });
      toast.success(t('crypto.success'));
      setOpen(false);
      setForm({ name: '', contact: '' });
    } catch (err) {
      toast.error(t('form.error'));
    } finally {
      setSending(false);
    }
  };

  const inputCls = 'w-full rounded-lg bg-[#0F172A] border border-slate-700 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-blue-500/70 focus:ring-2 focus:ring-blue-500/20 transition-colors';

  return (
    <div className="rounded-xl border border-slate-700/70 bg-[#0F172A] p-6 text-left" data-testid="crypto-payment">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="flex w-9 h-9 items-center justify-center rounded-lg bg-emerald-500/15 border border-emerald-500/30">
            <Coins className="w-5 h-5 text-emerald-400" />
          </span>
          <div>
            <p className="font-display text-lg text-white">{t('crypto.title')}</p>
            <p className="text-[11px] font-semibold tracking-widest text-emerald-400">{t('crypto.network')}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-semibold tracking-widest text-slate-500">{t('crypto.amount')}</p>
          <p className="font-display text-2xl text-white" data-testid="crypto-amount">{amount}</p>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-4">
        <div className="shrink-0 rounded-lg bg-white p-2">
          <QRCodeSVG value={WALLET} size={88} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold tracking-widest text-slate-500 mb-1.5">{t('crypto.walletLabel')}</p>
          <p className="font-mono text-[11px] text-slate-300 break-all leading-relaxed" data-testid="crypto-wallet">{WALLET}</p>
          <button
            data-testid="crypto-copy-btn"
            onClick={copy}
            className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg border border-slate-600 px-3 py-1.5 text-xs font-semibold text-slate-200 transition-colors hover:border-emerald-500/60 hover:text-emerald-300"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? t('crypto.copied') : t('crypto.copy')}
          </button>
        </div>
      </div>

      <p className="mt-4 text-xs text-slate-500 leading-relaxed">{t('crypto.hint')}</p>
      <button
        data-testid="crypto-paid-btn"
        onClick={() => setOpen(true)}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_25px_rgba(16,185,129,0.4)] active:scale-[0.98]"
      >
        <Send className="w-4 h-4" />
        {t('crypto.paidBtn')}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1E293B] border border-slate-700 text-slate-100 sm:max-w-md" data-testid="crypto-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">{t('crypto.modalTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400">{t('crypto.modalSub')}</p>
          <form onSubmit={submit} className="space-y-4 mt-3">
            <input
              data-testid="crypto-name-input"
              className={inputCls}
              placeholder={t('form.name')}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
            <input
              data-testid="crypto-contact-input"
              className={inputCls}
              placeholder={t('form.telegram')}
              value={form.contact}
              onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
              required
            />
            <button
              data-testid="crypto-submit-btn"
              type="submit"
              disabled={sending}
              className="w-full rounded-lg bg-emerald-500 px-6 py-3.5 text-base font-semibold text-white transition-all duration-300 hover:bg-emerald-400 active:scale-[0.98] disabled:opacity-60"
            >
              {sending ? t('form.sending') : t('crypto.send')}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
